import {
  deleteDoc,
  doc,
  getDoc,
  serverTimestamp,
  writeBatch,
} from 'firebase/firestore';
import { db } from '../firebase';
import { buildDefaultProfile, normalizeProfile, normalizeUsername } from '../utils/profileDefaults';

function requireDb() {
  if (!db) {
    throw new Error('Firestore is not configured. Add REACT_APP_FIREBASE_* values to .env.local.');
  }
}

function profileRef(uid) {
  return doc(db, 'profiles', uid);
}

function usernameRef(usernameLower) {
  return doc(db, 'usernames', usernameLower);
}

function emailLookupRef(email) {
  return doc(db, 'emailLookups', email.trim().toLowerCase());
}

function emailLookupData(uid, profileData, now) {
  const email = String(profileData.email || '').trim().toLowerCase();
  if (!email) {
    return null;
  }
  return {
    ref: emailLookupRef(email),
    data: {
      uid,
      emailKey: email,
      email,
      username: profileData.usernameLower,
      displayName: profileData.displayName,
      createdAt: profileData.createdAt || now,
      updatedAt: now,
    },
  };
}

export async function getUserProfile(uid) {
  requireDb();
  const snapshot = await getDoc(profileRef(uid));
  if (!snapshot.exists()) {
    return null;
  }
  return normalizeProfile(snapshot.data());
}

export async function createUserProfile(user, input = {}) {
  requireDb();
  const profile = normalizeProfile(buildDefaultProfile(user, input));
  const usernameLower = normalizeUsername(profile.username);
  if (!usernameLower || usernameLower.length < 3) {
    throw new Error('Username must be at least 3 letters or numbers.');
  }

  const existingUsername = await getDoc(usernameRef(usernameLower));
  if (existingUsername.exists() && existingUsername.data().uid !== user.uid) {
    throw new Error('That username is already taken.');
  }

  const now = serverTimestamp();
  const profileData = {
    ...profile,
    username: usernameLower,
    usernameLower,
    createdAt: now,
    updatedAt: now,
  };

  const batch = writeBatch(db);
  batch.set(profileRef(user.uid), profileData);
  batch.set(usernameRef(usernameLower), {
    uid: user.uid,
    username: usernameLower,
    displayName: profileData.displayName,
    createdAt: now,
    updatedAt: now,
  });
  const lookup = emailLookupData(user.uid, profileData, now);
  if (lookup) {
    batch.set(lookup.ref, lookup.data);
  }
  await batch.commit();

  return profileData;
}

export async function isUsernameAvailable(username, currentUid = null) {
  requireDb();
  const usernameLower = normalizeUsername(username);
  if (!usernameLower || usernameLower.length < 3) {
    return false;
  }
  const snapshot = await getDoc(usernameRef(usernameLower));
  return !snapshot.exists() || snapshot.data().uid === currentUid;
}

export async function ensureUserProfile(user, input = {}) {
  const existing = await getUserProfile(user.uid);
  if (existing) {
    return existing;
  }
  return createUserProfile(user, input);
}

export async function saveUserProfile(uid, profile) {
  requireDb();
  const current = await getUserProfile(uid);
  const usernameLower = normalizeUsername(profile.username);
  if (!usernameLower || usernameLower.length < 3) {
    throw new Error('Username must be at least 3 letters or numbers.');
  }

  const existingUsername = await getDoc(usernameRef(usernameLower));
  if (existingUsername.exists() && existingUsername.data().uid !== uid) {
    throw new Error('That username is already taken.');
  }

  const now = serverTimestamp();
  const profileData = normalizeProfile({
    ...profile,
    ownerId: uid,
    username: usernameLower,
    usernameLower,
    updatedAt: now,
    createdAt: current?.createdAt || now,
  });

  const batch = writeBatch(db);
  batch.set(profileRef(uid), profileData, { merge: true });
  batch.set(usernameRef(usernameLower), {
    uid,
    username: usernameLower,
    displayName: profileData.displayName,
    updatedAt: now,
  }, { merge: true });
  const lookup = emailLookupData(uid, profileData, now);
  if (lookup) {
    batch.set(lookup.ref, lookup.data, { merge: true });
  }

  if (current?.usernameLower && current.usernameLower !== usernameLower) {
    batch.delete(usernameRef(current.usernameLower));
  }

  await batch.commit();
  return profileData;
}

export async function getPublicProfileByUsername(username) {
  requireDb();
  const usernameLower = normalizeUsername(username);
  const usernameSnapshot = await getDoc(usernameRef(usernameLower));
  if (!usernameSnapshot.exists()) {
    return null;
  }

  const { uid } = usernameSnapshot.data();
  const profileSnapshot = await getDoc(profileRef(uid));
  if (!profileSnapshot.exists()) {
    return null;
  }

  const profile = normalizeProfile(profileSnapshot.data());
  if (profile.visibility !== 'public') {
    return null;
  }

  return profile;
}

export async function deleteUsernameReservation(usernameLower) {
  requireDb();
  await deleteDoc(usernameRef(usernameLower));
}
